import { clamp } from "../sim/math";

// Continuous strategic-zoom camera (GDD §8.2). One camera from tactical close-up
// to whole-map icon view; sprites swap to strategic icons below ICON_ZOOM.
// Zoom is eased toward a target anchored under the cursor for a smooth feel.

export const ICON_ZOOM = 0.82; // below this, units render as strategic icons

export class Camera {
  x = 0; // world coords at screen centre
  y = 0;
  zoom = 1.4; // pixels per world unit
  private targetZoom = 1.4;
  private anchorSx = 0;
  private anchorSy = 0;
  private anchorWx = 0;
  private anchorWy = 0;
  private zooming = false;
  private userZoomed = false;
  minZoom = 0.45;
  maxZoom = 3.2;
  screenW = 800;
  screenH = 600;
  private worldW: number;
  private worldH: number;

  constructor(worldW: number, worldH: number) {
    this.worldW = worldW;
    this.worldH = worldH;
    this.x = worldW / 2;
    this.y = worldH / 2;
  }

  /** Zoom that makes the world fully cover the viewport (no empty margins). */
  coverZoom(): number {
    return Math.max(this.screenW / this.worldW, this.screenH / this.worldH);
  }
  /** Zoom that fits the whole world inside the viewport (framed in the void). */
  containZoom(): number {
    return Math.min(this.screenW / this.worldW, this.screenH / this.worldH);
  }

  resize(w: number, h: number): void {
    this.screenW = w;
    this.screenH = h;
    // The map is the world edge: never zoom out past cover fit, so the
    // terrain always fills the viewport and no void is ever visible.
    this.minZoom = this.coverZoom();
    if (!this.userZoomed) {
      // Default: fill the whole screen with terrain (cover fit) — a full-screen game.
      this.zoom = this.targetZoom = clamp(this.coverZoom() * 1.05, this.minZoom, this.maxZoom);
    } else {
      this.zoom = this.targetZoom = clamp(this.zoom, this.minZoom, this.maxZoom);
    }
    this.clampToWorld();
  }

  worldToScreenX(wx: number): number {
    return (wx - this.x) * this.zoom + this.screenW / 2;
  }
  worldToScreenY(wy: number): number {
    return (wy - this.y) * this.zoom + this.screenH / 2;
  }
  screenToWorldX(sx: number): number {
    return (sx - this.screenW / 2) / this.zoom + this.x;
  }
  screenToWorldY(sy: number): number {
    return (sy - this.screenH / 2) / this.zoom + this.y;
  }

  isIconZoom(): boolean {
    return this.zoom < ICON_ZOOM;
  }
  /** 0 at ICON_ZOOM, 1 well below — used to fade sprites into icons smoothly. */
  iconBlend(): number {
    return clamp((ICON_ZOOM - this.zoom) / (ICON_ZOOM * 0.4), 0, 1);
  }

  pan(dxScreen: number, dyScreen: number): void {
    this.x += dxScreen / this.zoom;
    this.y += dyScreen / this.zoom;
    this.clampToWorld();
  }

  panWorld(dx: number, dy: number): void {
    this.x += dx;
    this.y += dy;
    this.clampToWorld();
  }

  /** Request an eased zoom toward a screen anchor (keeps the point under it). */
  zoomAt(factor: number, anchorSx: number, anchorSy: number): void {
    this.userZoomed = true;
    this.targetZoom = clamp(this.targetZoom * factor, this.minZoom, this.maxZoom);
    this.anchorSx = anchorSx;
    this.anchorSy = anchorSy;
    // World point currently under the cursor — hold it fixed through the zoom.
    this.anchorWx = this.screenToWorldX(anchorSx);
    this.anchorWy = this.screenToWorldY(anchorSy);
    this.zooming = true;
  }

  /** Ease zoom toward target each render frame. */
  update(dt: number): void {
    if (!this.zooming) return;
    const k = 1 - Math.pow(0.0025, dt); // smooth, frame-rate independent
    this.zoom += (this.targetZoom - this.zoom) * k;
    if (Math.abs(this.targetZoom - this.zoom) < 0.0008) {
      this.zoom = this.targetZoom;
      this.zooming = false;
    }
    // Re-anchor so the cursor's world point stays under the cursor.
    this.x = this.anchorWx - (this.anchorSx - this.screenW / 2) / this.zoom;
    this.y = this.anchorWy - (this.anchorSy - this.screenH / 2) / this.zoom;
    this.clampToWorld();
  }

  centerOn(wx: number, wy: number): void {
    this.x = wx;
    this.y = wy;
    this.clampToWorld();
  }

  private clampToWorld(): void {
    // Per-axis: if the view is wider than the world, centre the world (symmetric
    // margins, filled by the out-of-bounds backdrop); else keep the view inside.
    const halfW = this.screenW / this.zoom / 2;
    const halfH = this.screenH / this.zoom / 2;
    if (halfW * 2 >= this.worldW) this.x = this.worldW / 2;
    else this.x = clamp(this.x, halfW, this.worldW - halfW);
    if (halfH * 2 >= this.worldH) this.y = this.worldH / 2;
    else this.y = clamp(this.y, halfH, this.worldH - halfH);
  }

  /** Visible world-space rectangle. */
  viewRect(): { x0: number; y0: number; x1: number; y1: number } {
    return {
      x0: this.screenToWorldX(0),
      y0: this.screenToWorldY(0),
      x1: this.screenToWorldX(this.screenW),
      y1: this.screenToWorldY(this.screenH),
    };
  }
}
